import React, { useEffect, useMemo, useRef } from "react";
import styles from "../Graph.module.css";

export type NodeId = string;
export interface Edge {
  from: NodeId;
  to: NodeId;
}

type TraversalType = "bfs" | "dfs" | null;

interface DirectedGraphEngineProps {
  nodes: NodeId[];
  edges: Edge[];
  traversal: TraversalType;
  startNode: NodeId | null;
  resetSignal: number;
  highlightedNodes: NodeId[];
  setHighlightedNodes: React.Dispatch<React.SetStateAction<NodeId[]>>;
  setTraversalResult: React.Dispatch<React.SetStateAction<NodeId[]>>;
}

type Adjacency = Record<NodeId, NodeId[]>;

function buildAdjacency(nodes: NodeId[], edges: Edge[]): Adjacency {
  const adj: Adjacency = {};
  for (const n of nodes) adj[n] = [];
  for (const { from, to } of edges) {
    if (!adj[from]) adj[from] = [];
    if (!adj[from].includes(to)) adj[from].push(to);
  }
  return adj;
}

function bfs(adj: Adjacency, start: NodeId): NodeId[] {
  const visited: NodeId[] = [];
  const queue: NodeId[] = [start];
  const seen = new Set<NodeId>([start]);

  while (queue.length > 0) {
    const node = queue.shift()!;
    visited.push(node);

    for (const neighbor of adj[node] ?? []) {
      if (!seen.has(neighbor)) {
        seen.add(neighbor);
        queue.push(neighbor);
      }
    }
  }
  return visited;
}

function dfs(adj: Adjacency, start: NodeId): NodeId[] {
  const visited: NodeId[] = [];
  const seen = new Set<NodeId>();

  function dfsVisit(node: NodeId) {
    seen.add(node);
    visited.push(node);

    for (const neighbor of adj[node] ?? []) {
      if (!seen.has(neighbor)) dfsVisit(neighbor);
    }
  }
  dfsVisit(start);
  return visited;
}

const DirectedGraphEngine: React.FC<DirectedGraphEngineProps> = ({
  nodes,
  edges,
  traversal,
  startNode,
  resetSignal,
  highlightedNodes,
  setHighlightedNodes,
  setTraversalResult,
}) => {
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const positions = useMemo(() => {
    const map: Record<NodeId, { x: number; y: number }> = {};
    if (nodes.length === 0) return map;

    const centerX = 200;
    const centerY = 130;
    const radius = 110;

    nodes.forEach((node, index) => {
      const angle = (2 * Math.PI * index) / nodes.length - Math.PI / 2;
      map[node] = {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    });
    return map;
  }, [nodes]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  useEffect(() => {
    if (!traversal) return;
    clearTimers();
    setHighlightedNodes([]);
    setTraversalResult([]);

    if (!startNode || nodes.length === 0) return;

    const adj = buildAdjacency(nodes, edges);
    const order =
      traversal === "bfs" ? bfs(adj, startNode) : dfs(adj, startNode);
    const STEP_MS = 500;

    order.forEach((nodeId, idx) => {
      const t = setTimeout(() => {
        setHighlightedNodes((prev) => [...prev, nodeId]);
        setTraversalResult((prev) => [...prev, nodeId]);
      }, idx * STEP_MS);
      timersRef.current.push(t);
    });
    return clearTimers;
  }, [
    traversal,
    nodes,
    edges,
    setHighlightedNodes,
    setTraversalResult,
  ]);

  useEffect(() => {
    clearTimers();
    setHighlightedNodes([]);
    setTraversalResult([]);
  }, [resetSignal, setHighlightedNodes, setTraversalResult]);

  if (nodes.length === 0) {
    return <svg className={styles.graphSvg} viewBox="0 0 400 260" />;
  }

  return (
    <svg className={styles.graphSvg} viewBox="0 0 400 260">
      <defs>
        <marker
          id="arrowhead"
          markerWidth="6"
          markerHeight="6"
          refX="3"
          refY="3"
          orient="auto"
        >
          <path d="M 0 0 L 6 3 L 0 6 z" fill="#cbd5f5" />
        </marker>
      </defs>

      {edges.map((edge, idx) => {
        const p1 = positions[edge.from];
        const p2 = positions[edge.to];
        if (!p1 || !p2) return null;
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const nodeRadius = 20;        
        const arrowPadding = 10;     
        const shorten = nodeRadius + arrowPadding;
        const endX = p2.x - (dx / len) * shorten;
        const endY = p2.y - (dy / len) * shorten;
        return (
          <line
            key={idx}
            x1={p1.x}
            y1={p1.y}
            x2={endX}
            y2={endY}
            className={styles.graphEdge}
            markerEnd="url(#arrowhead)"
          />
        );
      })}

      {nodes.map((node) => {
        const pos = positions[node];
        const isHighlighted = highlightedNodes.includes(node);
        
        return (
          <g key={node} transform={`translate(${pos.x}, ${pos.y})`}>
            <circle
              r={20}
              className={
                isHighlighted
                  ? `${styles.graphNode} ${styles.graphNodeHighlighted}`
                  : styles.graphNode
              }
            />
            <text dy="0.35em" className={styles.graphNodeLabel}>
              {node}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default DirectedGraphEngine;
